import React, { ChangeEvent, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  useBanner,
} from '@tq/ui-components';

import { isEmailValid } from 'tools';
import { AddTeamRequest, useAddTeam } from 'api';
import { useTeamMessageProps } from '../../hooks';
import { Organisation } from 'types';
import { BANNER_TIMEOUT } from 'config';

export type AddTeamProps = {
  organisation?: Organisation;
};

export const AddTeam: React.FC<AddTeamProps> = ({ organisation }) => {
  const { t } = useTranslation('manage_organisation');
  const banner = useBanner();

  const [addTeamRequest, SetAddTeamRequest] = useState<AddTeamRequest>();
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [teamEmailDisabled, setTeamEmailDisabled] = useState<boolean>(true);
  const [teamName, setTeamName] = useState<string>('');
  const [teamNameInvalid, setTeamNameInvalid] = useState<boolean>(false);
  const [teamEmail, setTeamEmail] = useState<string>('');
  const [teamEmailInvalid, setTeamEmailInvalid] = useState<boolean>(false);
  const [backOfficeCode, setBackOfficeCode] = useState<string>('');
  const [backOfficeCodeInvalid, setBackOfficeCodeInvalid] = useState<boolean>(false);

  const isNameValid = (value: string) => value.length > 0;

  const { mutate: teamMutation, status, error, isError, isSuccess, reset } = useAddTeam();

  const addTeamMessageProps = useTeamMessageProps(status, error, addTeamRequest);

  useEffect(() => {
    if (organisation?.id) {
      setIsDisabled(false);
      if (isEmailValid(organisation.email)) {
        setTeamEmail('');
        setTeamEmailDisabled(true);
        setTeamEmailInvalid(false);
      } else {
        setTeamEmailDisabled(false);
      }
    }
  }, [organisation]);

  useEffect(() => {
    const isFormInvalidExcludingEmail = !isNameValid(teamName) || !isNameValid(backOfficeCode);
    if (teamEmailDisabled) {
      setSubmitDisabled(isFormInvalidExcludingEmail);
    } else {
      setSubmitDisabled(isFormInvalidExcludingEmail || !isEmailValid(teamEmail));
    }
  }, [teamName, backOfficeCode, teamEmailDisabled, teamEmail]);

  const emptyForm = teamName + teamEmail + backOfficeCode === '';

  useEffect(() => {
    if (emptyForm) {
      setTeamNameInvalid(false);
      setBackOfficeCodeInvalid(false);
      setTeamEmailInvalid(false);
    }
  }, [emptyForm]);

  useEffect(() => {
    const attentionMessageDescription = (
      <>
        {addTeamMessageProps.messageErrors?.map((errmsg, idx) => (
          <li key={idx}>{errmsg}</li>
        ))}
      </>
    );

    if (
      (isError || isSuccess) &&
      (addTeamMessageProps.messageStatus === 'success' ||
        addTeamMessageProps.messageStatus === 'error')
    ) {
      banner({
        id: 'add_team_banner_id',
        title: addTeamMessageProps.messageTitle,
        status: addTeamMessageProps.messageStatus,
        description: attentionMessageDescription,
        duration: BANNER_TIMEOUT,
      });
      reset();
    }
  }, [
    addTeamMessageProps,
    addTeamRequest,
    isError,
    isSuccess,
    t,
    teamMutation,
    banner,
    reset,
  ]);

  const handleTeamNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTeamName(e.currentTarget.value);
  };

  const handleTeamNameBlur = (e: ChangeEvent<HTMLInputElement>) => {
    if (!emptyForm) setTeamNameInvalid(!isNameValid(e.currentTarget.value));
  };

  const handleBackofficeCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBackOfficeCode(e.currentTarget.value);
  };

  const handleBackofficeCodeBlur = (e: ChangeEvent<HTMLInputElement>) => {
    if (!emptyForm) setBackOfficeCodeInvalid(!isNameValid(e.currentTarget.value));
  };

  const handleTeamEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTeamEmail(e.currentTarget.value);
  };

  const handleTeamEmailBlur = (e: ChangeEvent<HTMLInputElement>) => {
    if (!emptyForm) setTeamEmailInvalid(!isEmailValid(e.currentTarget.value));
  };

  const resetTeamForm = () => {
    setTeamName('');
    setBackOfficeCode('');
    setTeamEmail('');
  };

  const handleSubmit = () => {
    if (organisation) {
      const request: AddTeamRequest = {
        organisationId: organisation.id,
        teamName: teamName,
        teamEmail: teamEmail,
        backOfficeTeamName: backOfficeCode,
      };
      SetAddTeamRequest(request);
      teamMutation(request, {
        onSuccess: () => resetTeamForm(),
      });
    }
  };

  return (
    <Card my={8} p={6}>
      <HStack width='100%' spacing={5} alignItems='start'>
        <Flex width='100%' gap={8}>
          <FormControl
            isDisabled={isDisabled}
            isInvalid={teamNameInvalid && !isDisabled}
            flexGrow={1}
          >
            <FormLabel htmlFor='name-input'>{`${t('team_name', 'Team name')}:`}</FormLabel>
            <Input
              id='name-input'
              disabled={isDisabled}
              value={teamName}
              onChange={handleTeamNameChange}
              onBlur={handleTeamNameBlur}
              placeholder={t('team_name_placeholder', 'Name')}
              data-testid='team-name-input'
            />
            <FormErrorMessage>
              {t('team_name_input_error', 'Please add a team name to continue')}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            isDisabled={isDisabled}
            isInvalid={backOfficeCodeInvalid && !isDisabled}
            flexGrow={1}
          >
            <FormLabel htmlFor='code-input'>
              {`${t('lpa_backoffice_code', 'LPA Backoffice code')}:`}
            </FormLabel>
            <Input
              id='code-input'
              disabled={isDisabled}
              value={backOfficeCode}
              onChange={handleBackofficeCodeChange}
              onBlur={handleBackofficeCodeBlur}
              placeholder={t('backoffice_code_placeholder', 'Back Office code')}
              data-testid='lpa-backoffice-code-input'
            />
            <FormErrorMessage>
              {t(
                'lpa_backoffice_code_input_error',
                'Please add a LPA backoffice code to continue'
              )}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={teamEmailInvalid && !teamEmailDisabled}
            isDisabled={teamEmailDisabled}
            flexGrow={1}
          >
            <FormLabel htmlFor='email-input'>{`${t('team_email', 'Team email')}:`}</FormLabel>
            <Input
              id='email-input'
              disabled={teamEmailDisabled}
              value={teamEmail}
              onChange={handleTeamEmailChange}
              onBlur={handleTeamEmailBlur}
              placeholder={t('team_email_placeholder', 'Email address')}
              data-testid='team-email-input'
            />
            <FormErrorMessage>
              {t('team_email_input_error', 'Please enter a valid email address')}
            </FormErrorMessage>
          </FormControl>
        </Flex>
        <Box flex={1} pt={9}>
          <Button
            disabled={submitDisabled}
            onClick={handleSubmit}
            data-testid='add-team-command'
          >
            {t('add_team', 'Add team')}
          </Button>
        </Box>
      </HStack>
    </Card>
  );
};
